import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import Button from '@/components/ui/Button'
import Input from '@/components/ui/Input'
import { userApi } from '@/services/api'
import styles from './ChildProfile.module.css'

export default function ChildProfile() {
  const [name, setName] = useState('')
  const [dob, setDob] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [hasExistingProfile, setHasExistingProfile] = useState(false)
  const navigate = useNavigate()

  // Load existing child profile if one exists
  useEffect(() => {
    userApi.getChildProfile()
      .then((res) => {
        const profile = res.data as { name?: string; date_of_birth?: string }
        if (profile.name) setName(profile.name)
        if (profile.date_of_birth) setDob(profile.date_of_birth)
        setHasExistingProfile(true)
      })
      .catch(() => {
        // No existing profile — that's fine
      })
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!name.trim()) {
      setError('Please enter your child\'s name')
      return
    }

    if (!dob) {
      setError('Please enter your child\'s date of birth')
      return
    }

    setLoading(true)

    try {
      const profileData = { name: name.trim(), date_of_birth: dob }

      if (hasExistingProfile) {
        await userApi.updateChildProfile(profileData)
      } else {
        try {
          await userApi.createChildProfile(profileData)
        } catch (err: unknown) {
          const axiosErr = err as { response?: { status?: number } }
          if (axiosErr?.response?.status === 409) {
            // Profile was created between our check and now — update instead
            await userApi.updateChildProfile(profileData)
          } else {
            throw err
          }
        }
      }
      navigate('/onboarding/questionnaire')
    } catch {
      setError('Failed to save. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Tell us about your child</h1>
        <p className={styles.subtitle}>
          This helps us personalize activities for your little one
        </p>
      </div>

      <form onSubmit={handleSubmit} className={styles.form}>
        <Input
          label="Child's name"
          placeholder="Enter your child's name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
        />

        <Input
          label="Date of birth"
          type="date"
          value={dob}
          onChange={(e) => setDob(e.target.value)}
          max={new Date().toISOString().split('T')[0]}
        />

        {error && <p className={styles.error}>{error}</p>}

        <Button type="submit" fullWidth size="lg" loading={loading}>
          Continue
        </Button>
      </form>
    </div>
  )
}
